import React from 'react';
import { Category, ProductCategoryExtension } from 'types/Category';
import { Product } from 'types/Product';
import Helmet from 'react-helmet';
import { auLogoURL } from './getBaseSchemaOrgTags';
import { useProductSelectors, YotpoProductBottomLine } from 'hooks/useProductSelectors';
import { isCard } from '../../features/pdp';
import { getStrikethroughPrice } from 'helpers/strikethroughPricing';
import { ItemAvailability } from 'helpers/seo';

const getProductOffers = (product: Product) => {
  // TODO: Update for CLP
  let listPrice: number;
  let strikethroughPrice: number;
  if (isCard(product) && product?.tierPrices?.length) {
    const { price, quantity } = product.tierPrices[0];
    listPrice = price * quantity;
    strikethroughPrice = getStrikethroughPrice(product.strikethroughValues, price, product.sku) * quantity;
  } else {
    strikethroughPrice = getStrikethroughPrice(product.strikethroughValues, product.price, product.sku);
    listPrice = product.price;
  }

  const lowPrice = strikethroughPrice ?? listPrice;
  const hasStrikethroughPrice = !!strikethroughPrice;
  const isOutOfStock = product?.inStock === false;

  return {
    '@type': 'Offer',
    priceCurrency: 'USD',
    price: lowPrice.toFixed(2),
    availability: isOutOfStock ? ItemAvailability.OUT_OF_STOCK : ItemAvailability.IN_STOCK,
    ...(hasStrikethroughPrice && {
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        priceType: 'https://schema.org/ListPrice',
        price: listPrice.toFixed(2),
        priceCurrency: 'USD'
      }
    })
  };
};

function getProductPrice(product: Product) {
  return product.lowestPrice || product.price || 0;
}

function calculateCategoryPriceRange(category: Category) {
  return category.products.reduce(
    (range: { lowest: number; highest: number }, product: Product) => {
      const price = getProductPrice(product);
      if (!range.lowest || price < range.lowest) {
        range.lowest = price;
      }
      if (!range.highest || price > range.highest) {
        range.highest = price;
      }
      return range;
    },
    { lowest: 0, highest: 0 }
  );
}

function getProductsInfo(category: Category, getYotpoProductBottomline: (productId: number) => YotpoProductBottomLine) {
  return category.products.map(product => {
    const yotpoProductBottomLine = getYotpoProductBottomline(product?.productId);
    return {
      '@type': 'Product',
      '@id': 'https://www.artifactuprising.com/' + (product.canonicalUrl ?? product.url),
      name: product.name,
      description: product.metaDescription,
      image: product.image,
      url: 'https://www.artifactuprising.com/' + (product.canonicalUrl ?? product.url),
      offers: getProductOffers(product),
      ...(yotpoProductBottomLine && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: yotpoProductBottomLine.score.toFixed(2),
          reviewCount: yotpoProductBottomLine.totalReviews
        }
      })
    };
  });
}

function getFAQInfo(category: ProductCategoryExtension) {
  if (category.frequentlyAskedQuestions) {
    const mappedEntities = category.frequentlyAskedQuestions.map(faq => {
      return {
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      };
    });

    return {
      '@context': 'https://schema.org/',
      '@type': 'FAQPage',
      mainEntity: mappedEntities
    };
  }
  return false;
}

function getSchemaOrgValuesForCategory(
  category: Category & ProductCategoryExtension,
  getYotpoProductBottomline: (productId: number) => YotpoProductBottomLine
) {
  const priceRange = calculateCategoryPriceRange(category);
  // Because not all Categories have a primary image available, for now we are
  // Just using the primary image of the first available product. We fall back
  // To the A/U logo in the very worst-case.
  const firstProductImage = category.products[0] ? category.products[0].image : auLogoURL;

  const productsInfo = getProductsInfo(category, getYotpoProductBottomline);

  return {
    '@context': 'https://schema.org/',
    '@type': 'ProductCollection',
    name: category.title || category.name,
    description: category.metaDescription,
    image: firstProductImage,
    provider: {
      '@type': 'Organization',
      name: 'Artifact Uprising',
      id: 'https://www.artifactuprising.com/'
    },
    collectionSize: category.products.length,
    ...(category.totalReviews &&
      category.averageReviewScore && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: category.averageReviewScore,
          reviewCount: category.totalReviews
        }
      }),
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      itemCondition: 'https://schema.org/NewCondition',
      highPrice: priceRange.highest,
      lowPrice: priceRange.lowest
    },
    includesObject: productsInfo
  };
}

interface SchemaOrgCategoryProps {
  category: Category & ProductCategoryExtension;
}

export const SchemaOrgCategory: React.FC<SchemaOrgCategoryProps> = ({ category }: SchemaOrgCategoryProps) => {
  const { getYotpoProductBottomline } = useProductSelectors();

  const schemaOrgValues = JSON.stringify(
    getSchemaOrgValuesForCategory(category, getYotpoProductBottomline),
    null,
    '  '
  );

  //Only show FAQ info on pages where it exists
  if (category.frequentlyAskedQuestions && category.frequentlyAskedQuestions.length > 0) {
    const faqInfo = JSON.stringify(getFAQInfo(category), null, '  ');
    return (
      <Helmet>
        <script type="application/ld+json">{schemaOrgValues}</script>
        <script type="application/ld+json">{faqInfo}</script>
      </Helmet>
    );
  }

  return (
    <Helmet>
      <script type="application/ld+json">{schemaOrgValues}</script>
    </Helmet>
  );
};